import { uniq } from "@-/com.npmjs.array-utils-ts";
import { useQuery } from "@-/com.npmjs.tanstack__react-query";
import { hasLength } from "@-/com.npmjs.ts-array-length";
import { contentQueries } from "@-/dev.improve.haniverse.database.schemas.content.query";
import type { ReferenceGraphemeMorphemeSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { useActorUser } from "@-/dev.improve.haniverse.lib.user";
import { getPrimaryPinyinForGrapheme } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.glyph";
import { usePrevalentPinyin } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.pinyin.prevalence";
import { useMorphemeInfoPanelDictionaryGradeLabel } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.panel-content.views.dictionary.__sections.overview._sections.grade-badge";
import { useMemo } from "react";

export interface GraphemeDictionaryReading {
  definitions: string[];
  /** `HSK n` / `TBCL n` for this reading, or `null` when its curriculum doesn't list it. */
  gradeLabel: string | null;
  toneNumbersPinyin: string;
}

function splitDefinitions(entries: readonly string[]) {
  return entries.flatMap((entry) =>
    entry
      .split(";")
      .map((definition) => definition.trim())
      .filter(Boolean),
  );
}

/**
 * The character's dictionary readings, each with its own definitions and curriculum grade, plus
 * every definition across them. The dictionary keys proper-noun senses by a capitalized reading
 * (`Wang2`: a surname), so those join the lowercase reading's row after its common senses. The main
 * reading leads; the rest keep the dictionary's order. Obscure components keep their definitions
 * but have no readings worth listing.
 */
export function useGraphemeDictionaryReadings(graphemeCard: ReferenceGraphemeMorphemeSlice): {
  definitions: string[];
  readings: GraphemeDictionaryReading[];
} {
  const { hanziScript, hskVersion } = useActorUser();
  const cardGradeLabel = useMorphemeInfoPanelDictionaryGradeLabel(graphemeCard);
  const { definitionList } = graphemeCard;
  const hasPrevalentReadings = !hasLength(
    usePrevalentPinyin(graphemeCard.grapheme, definitionList),
    0,
  );
  // The dictionary only records the character's lowest grade; each reading's own grade lives on its
  // pronunciation card.
  const { data: pronunciationCards } = useQuery(
    contentQueries(import.meta.location).appGraphemePronunciationCard.findMany.useQueryOptions({
      where: { grapheme: graphemeCard.grapheme },
    }),
  );

  return useMemo(() => {
    const groups = new Map<string, { capitalized: string[]; common: string[] }>();
    for (const { entries, pinyin } of definitionList) {
      const toneNumbersPinyin = pinyin.toLowerCase();
      let group = groups.get(toneNumbersPinyin);
      if (group === undefined) {
        group = { capitalized: [], common: [] };
        groups.set(toneNumbersPinyin, group);
      }
      (pinyin === toneNumbersPinyin ? group.common : group.capitalized).push(
        ...splitDefinitions(entries),
      );
    }
    const mainPinyin =
      pronunciationCards?.find((card) => card.isMain)?.pinyin ??
      getPrimaryPinyinForGrapheme(graphemeCard.grapheme);
    const readings = Array.from(groups, ([toneNumbersPinyin, { capitalized, common }]) => {
      const card = pronunciationCards?.find((entry) => entry.pinyin === toneNumbersPinyin);
      let level: number | null = null;
      if (hanziScript === "traditional") {
        level = card?.tbclLevel ?? null;
      } else if (hskVersion === "hsk2026") {
        level = card?.hsk2026Level ?? null;
      }
      return {
        definitions: uniq([...common, ...capitalized]),
        gradeLabel:
          level === null ? null : `${hanziScript === "traditional" ? "TBCL" : "HSK"} ${level}`,
        toneNumbersPinyin,
      };
    });
    const mainIndex = readings.findIndex((reading) => reading.toneNumbersPinyin === mainPinyin);
    if (mainIndex > 0) {
      readings.unshift(...readings.splice(mainIndex, 1));
    }
    // Older HSK versions grade only the character, which is the grade of its main reading.
    const first = readings[0];
    if (first !== undefined && readings.every((reading) => reading.gradeLabel === null)) {
      first.gradeLabel = cardGradeLabel;
    }
    return {
      definitions: uniq(readings.flatMap((reading) => reading.definitions)),
      readings: hasPrevalentReadings ? readings : [],
    };
  }, [
    cardGradeLabel,
    definitionList,
    graphemeCard.grapheme,
    hanziScript,
    hasPrevalentReadings,
    hskVersion,
    pronunciationCards,
  ]);
}
